<template>
  <interview-layout :touch-and-validate="touchAndValidate">
    <div>
      <h1 class="px-4 fade-in-up-enter-active animation-slowest max-100 text-primary">
        Eisen van de subsidie
      </h1>
      <base-form @submit="onSubmit">
        <div class="d-flex flex-column flex-lg-row justify-content-center">
          <base-card inner-class="container-narrow w-100" color="alternate" :outer-padding="true">
            <base-card v-if="executionInfo.length" class="mb-4" inner-class="small" color="secondary">
              <checkmark-item
                v-for="info in executionInfo"
                :key="info.subsidy"
                class="mb-2"
                icon="info-circle-fill"
              >
                {{ info.message }}
              </checkmark-item>
            </base-card>

            <base-input
              v-for="question in visibleQuestions"
              :key="question.key"
              class="mb-4"
              :type="question.type || 'radiobuttons'"
              :options="question.options"
              :name="`${question.scope || 'subsidy'}-${question.key}`"
              :value="getQuestionValue(question)"
              @update:value="setQuestionValue(question, $event)"
              :validator="getQuestionValidator(question)"
            >
              {{ question.label }}
            </base-input>
          </base-card>
        </div>

        <button-row class="mt-4 flex-row-reverse">
          <base-button class="ms-auto" icon="bi-arrow-right" type="submit" :disabled="!isValid">Volgende</base-button>
          <base-button variant="minimal-secondary" icon="bi-arrow-left" icon-position="start" @click="onPrev">Terug</base-button>
        </button-row>
      </base-form>
    </div>
  </interview-layout>
</template>

<script>
import InterviewLayout from "@/views/layouts/InterviewLayout.vue";
import BaseCard from "@/components/BaseCard.vue";
import BaseForm from "@/components/BaseForm.vue";
import BaseButton from "@/components/BaseButton.vue";
import BaseInput from "@/components/BaseInput.vue";
import ButtonRow from "@/components/ButtonRow.vue";
import CheckmarkItem from "@/components/CheckmarkItem.vue";
import { CommonComposable } from "@/helpers/CommonComposable";
import { useAllStores } from "@/helpers/UseAllStores";
import { FlowService } from "@/services/FlowService";
import { MunicipalitySubsidyService } from "@/services/MunicipalitySubsidyService";

const EXECUTION_WINDOW_OPTIONS = [
  { label: "Ja", value: true },
  { label: "Nee", value: false },
];

const SUBSIDY_QUESTIONS = [{
  key: "measuresExecuted",
  label: "Zijn de maatregelen waarvoor u subsidie wilt aanvragen al uitgevoerd?",
  options: EXECUTION_WINDOW_OPTIONS,
}];

function formatDate(dateString) {
  return new Intl.DateTimeFormat("nl-NL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(`${dateString}T00:00:00`));
}

export default {
  components: {
    InterviewLayout,
    BaseCard,
    BaseForm,
    BaseButton,
    BaseInput,
    ButtonRow,
    CheckmarkItem,
  },
  setup(props, context) {
    return { ...CommonComposable(props, context), ...useAllStores() };
  },
  computed: {
    activeSubsidies() {
      return MunicipalitySubsidyService.getEligibleSubsidies(this.lead, "financingVhf");
    },
    activeQuestions() {
      if (!MunicipalitySubsidyService.executionChecksEnabled) return [];
      const questions = this.activeSubsidies.length ? [...SUBSIDY_QUESTIONS] : [];
      this.activeSubsidies.forEach((subsidy) => {
        const twkFrom = MunicipalitySubsidyService.getExecutionConfig(subsidy, this.lead)?.twkFrom;
        if (!twkFrom) return;

        questions.push({
          key: `twk-${subsidy}`,
          label: `Zijn de maatregelen uitgevoerd op of na ${formatDate(twkFrom)} voor ${subsidy}?`,
          options: EXECUTION_WINDOW_OPTIONS,
          dependsOn: "measuresExecuted",
          twkSubsidy: subsidy,
        });
      });

      return questions;
    },
    executionInfo() {
      return this.activeSubsidies
        .map((subsidy) => {
          const executionConfig = MunicipalitySubsidyService.getExecutionConfig(subsidy, this.lead);

          if (executionConfig?.application === "before") {
            return {
              subsidy,
              message: `Voor de subsidie '${subsidy}' komen alleen maatregelen in aanmerking die nog niet zijn uitgevoerd.`,
            };
          }

          if (executionConfig?.application === "after") {
            return {
              subsidy,
              message: `Voor de subsidie '${subsidy}' kunt u mogelijk ook subsidie aanvragen voor maatregelen die al zijn uitgevoerd.`,
            };
          }

          return null;
        })
        .filter(Boolean);
    },
    visibleQuestions() {
      return this.activeQuestions.filter((question) => {
        if (!question.dependsOn) {
          return true;
        }

        return this.lead.otherSubsidies.subsidy[question.dependsOn] === true;
      });
    },
    isValid() {
      return this.visibleQuestions.every(
        (question) => !this.getQuestionValidator(question).$invalid,
      );
    },
  },
  methods: {
    getQuestionValue(question) {
      if (question.twkSubsidy) {
        return this.lead.otherSubsidies.subsidy.twkEligibility?.[question.twkSubsidy];
      }

      return this.lead.otherSubsidies.subsidy[question.key];
    },
    setQuestionValue(question, value) {
      if (question.twkSubsidy) {
        this.lead.otherSubsidies.subsidy.twkEligibility[question.twkSubsidy] = value;
        return;
      }

      this.lead.otherSubsidies.subsidy[question.key] = value;
      if (question.key === "measuresExecuted" && value === false) {
        this.lead.otherSubsidies.subsidy.executedAt = null;
        this.lead.otherSubsidies.subsidy.twkEligibility = {};
      }
    },
    getQuestionValidator(question) {
      if (question.twkSubsidy) {
        return {
          $invalid: this.getQuestionValue(question) !== true && this.getQuestionValue(question) !== false,
          $touch() {},
        };
      }

      return this.v$.otherSubsidies.subsidy[question.key];
    },
    touchAndValidate() {
      this.visibleQuestions.forEach((question) => {
        this.getQuestionValidator(question).$touch();
      });

      return this.visibleQuestions.every(
        (question) => !this.getQuestionValidator(question).$invalid,
      );
    },
    onSubmit() {
      if (this.touchAndValidate()) {
        FlowService.pushNext(this);
      }
    },
    onPrev() {
      FlowService.pushPrev(this);
    },
  },
};
</script>

<style scoped lang="scss">
</style>
